import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import AdminLayout from "../../components/admin/AdminLayout";
import { programsApi } from "../../services/api";

function AdminPrograms() {
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    loadPrograms();
  }, []);

  const loadPrograms = async () => {
    try {
      const response = await programsApi.getAll(true); // Include inactive
      setPrograms(response.data);
    } catch (error) {
      console.error("Error loading programs:", error);
      alert("Failed to load programs");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (programId) => {
    navigate(`/admin/programs/${programId}/edit`);
  };

  const handleDelete = async (programId) => {
    if (
      !window.confirm(
        "Are you sure you want to delete this program? This action cannot be undone.",
      )
    ) {
      return;
    }

    try {
      await programsApi.delete(programId);
      alert("Program deleted successfully!");
      loadPrograms(); // Reload the list
    } catch (error) {
      console.error("Error deleting program:", error);
      alert("Failed to delete program");
    }
  };

  const toggleStatus = async (programId, currentStatus) => {
    try {
      const program = programs.find((p) => p.id === programId);
      if (!program) return;

      await programsApi.update(programId, {
        ...program,
        is_active: !currentStatus,
      });

      alert(
        `Program ${!currentStatus ? "activated" : "deactivated"} successfully!`,
      );
      loadPrograms(); // Reload the list
    } catch (error) {
      console.error("Error updating program status:", error);
      alert("Failed to update program status");
    }
  };

  const toggleFeatured = async (programId, currentFeatured) => {
    try {
      const program = programs.find((p) => p.id === programId);
      if (!program) return;

      await programsApi.update(programId, {
        ...program,
        featured: !currentFeatured,
      });

      alert(
        `Program ${
          !currentFeatured ? "added to" : "removed from"
        } featured list!`,
      );
      loadPrograms(); // Reload the list
    } catch (error) {
      console.error("Error updating featured status:", error);
      alert("Failed to update featured status");
    }
  };

  const viewProgram = (slug) => {
    window.open(`/programs/${slug}`, "_blank");
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              Programs Management
            </h1>
            <p className="text-gray-600">
              Manage your training programs and courses
            </p>
          </div>
          <Link
            to="/admin/programs/new"
            className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
          >
            + Add New Program
          </Link>
        </div>

        {/* Stats Summary */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-lg shadow p-4">
            <p className="text-sm text-gray-600">Total Programs</p>
            <p className="text-2xl font-bold">{programs.length}</p>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <p className="text-sm text-gray-600">Active</p>
            <p className="text-2xl font-bold text-green-600">
              {programs.filter((p) => p.is_active).length}
            </p>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <p className="text-sm text-gray-600">Featured</p>
            <p className="text-2xl font-bold text-[#3B82F6]">
              {programs.filter((p) => p.featured).length}
            </p>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <p className="text-sm text-gray-600">Average Price</p>
            <p className="text-2xl font-bold">
              BDT
              {(
                programs.reduce(
                  (sum, p) =>
                    sum +
                    (p.show_price !== false ? parseFloat(p.price) || 0 : 0),
                  0,
                ) / (programs.filter((p) => p.show_price !== false).length || 1)
              ).toFixed(2)}
            </p>
          </div>
        </div>

        {/* Featured Programs Section */}
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
          <h3 className="font-semibold text-yellow-800 mb-4">
            Featured Programs
          </h3>
          <p className="text-yellow-700 text-sm mb-4">
            Featured programs appear on the homepage. You can feature up to 3
            programs.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {programs
              .filter((p) => p.featured)
              .map((program) => (
                <div
                  key={program.id}
                  className="bg-white border rounded-lg p-4"
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="text-2xl">{program.icon}</span>
                        <h4 className="font-medium">{program.title}</h4>
                      </div>
                      <p className="text-sm text-gray-500 mt-1">
                        {program.duration}
                      </p>
                    </div>
                    <button
                      onClick={() =>
                        toggleFeatured(program.id, program.featured)
                      }
                      className="text-red-600 hover:text-red-800 text-sm px-2 py-1 bg-red-50 rounded"
                    >
                      Remove
                    </button>
                  </div>
                </div>
              ))}
            {programs.filter((p) => p.featured).length === 0 && (
              <p className="text-yellow-600 text-sm col-span-3">
                No featured programs yet. Click the "Feature" button on any
                program to add it here.
              </p>
            )}
          </div>
        </div>

        {loading ? (
          <div className="text-center py-8">Loading programs...</div>
        ) : (
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Program
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Duration & Price
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {programs.map((program) => (
                    <tr key={program.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <div className="flex items-center">
                          <span className="text-2xl mr-3">{program.icon}</span>
                          <div>
                            <div className="font-medium text-gray-900">
                              {program.title}
                            </div>
                            <div className="text-sm text-gray-500 truncate max-w-xs">
                              {program.description}
                            </div>
                            <div className="flex items-center mt-1 space-x-2">
                              {program.featured && (
                                <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-purple-100 text-purple-800">
                                  Featured
                                </span>
                              )}
                              <code className="text-xs text-gray-500">
                                /{program.slug}
                              </code>
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="space-y-1">
                          <div className="text-sm">
                            <span className="font-medium">Duration:</span>{" "}
                            {program.duration || "N/A"}
                          </div>
                          {Boolean(program.show_price) && (
                            <div className="text-sm">
                              <span className="font-medium">Price:</span>{" "}
                              {program.price != 0
                                ? `BDT ${program.price}`
                                : "Contact for pricing"}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="space-y-2">
                          <button
                            onClick={() =>
                              toggleFeatured(program.id, program.featured)
                            }
                            className={`block px-3 py-1 rounded-full text-xs font-semibold cursor-pointer ${
                              program.featured
                                ? "bg-purple-100 text-purple-800 hover:bg-purple-200"
                                : "bg-gray-100 text-gray-800 hover:bg-gray-200"
                            }`}
                          >
                            {program.featured ? "Featured" : "Make Featured"}
                          </button>
                          <button
                            onClick={() =>
                              toggleStatus(program.id, program.is_active)
                            }
                            className={`block px-3 py-1 rounded-full text-xs font-semibold cursor-pointer ${
                              program.is_active
                                ? "bg-green-100 text-green-800 hover:bg-green-200"
                                : "bg-red-100 text-red-800 hover:bg-red-200"
                            }`}
                          >
                            {program.is_active ? "Active" : "Inactive"}
                          </button>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex space-x-2">
                          <button
                            onClick={() => handleEdit(program.id)}
                            className="text-blue-600 hover:text-blue-900 text-sm font-medium px-3 py-1 bg-blue-50 rounded hover:bg-blue-100"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => viewProgram(program.slug)}
                            className="text-green-600 hover:text-green-900 text-sm font-medium px-3 py-1 bg-green-50 rounded hover:bg-green-100"
                          >
                            View
                          </button>
                          <button
                            onClick={() => handleDelete(program.id)}
                            className="text-red-600 hover:text-red-900 text-sm font-medium px-3 py-1 bg-red-50 rounded hover:bg-red-100"
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Empty State */}
        {!loading && programs.length === 0 && (
          <div className="text-center py-12">
            <div className="text-gray-400 text-4xl mb-4">🎓</div>
            <h3 className="text-lg font-semibold text-gray-700 mb-2">
              No programs yet
            </h3>
            <p className="text-gray-500 mb-6">
              Create your first training program to start offering courses.
            </p>
            <Link
              to="/admin/programs/new"
              className="inline-block bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700"
            >
              Create Your First Program
            </Link>
          </div>
        )}

        {/* Instructions */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
          <h3 className="font-semibold text-blue-800 mb-2">
            How to Manage Programs
          </h3>
          <ul className="space-y-2 text-sm text-blue-700">
            <li>
              • <strong>Edit</strong> - Click edit to modify program details,
              pricing, and content
            </li>
            <li>
              • <strong>Feature</strong> - Featured programs appear on the
              homepage (max 3)
            </li>
            <li>
              • <strong>Activate/Deactivate</strong> - Show or hide programs on
              the website
            </li>
            <li>
              • <strong>View</strong> - Open the program page in a new tab to
              preview it
            </li>
            <li>
              • <strong>Delete</strong> - Permanently remove a program (use with
              caution)
            </li>
            <li>
              • <strong>Icons</strong> - Choose an icon that represents your
              program
            </li>
            <li>
              • <strong>Display Order</strong> - Set the order in which programs
              appear (lower numbers first)
            </li>
          </ul>
        </div>
      </div>
    </AdminLayout>
  );
}

export default AdminPrograms;
